import React, { useRef, useEffect } from 'react';
import { graphql } from 'gatsby';
import { useTranslation, Trans } from 'react-i18next';

import MetatagsForLayout from '../components/MetatagsForLayout';
import useTransformGatsbyData from '../hooks/useTransformGatsbyData';
import useStrapiPageData from '../hooks/data/useStrapiPageData';
import useNavigation from '../hooks/useNavigation';
import useSearch from '../hooks/useSearch';
import useWithGA from '../hooks/useWithGA';

import { SystemGuide } from '../ui/templates/systemGuide/SystemGuide';

import CONSTANTS_NUMBERS from '../constants/numbers';
import BusinesswithSystemguide from '../components/BusinesswithSystemguide';

export const pageQuery = graphql`
  query SystemGuideData($category: String!, $pageBuildId: String!) {
    categoryData: bwCategory(id: { eq: $category }) {
      data
    }
    bwPageBuildData(id: { eq: $pageBuildId }) {
      data
    }
  }
`;

const removeP = (str) => {
  return str.replace(/<p>/g, '').replace(/<\/p>/g, '');
};

const SystemGuideTemplate = ({ location, pageContext, data }) => {
  const categoryData = useTransformGatsbyData(data.categoryData);
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const { savePageView } = useWithGA();

  const { siteUrls } = bwPageBuildData.pageData;

  const { t } = useTranslation();
  const { locale } = pageContext;
  const { menu, footer } = useNavigation(siteUrls, location);
  const search = useSearch();
  const systemguideRef = useRef(null);

  const {
    categoryName,
    categoryDbName,
    guideTitle,
    guideText,
    guideH1,
    guideIntroText,
  } = categoryData;

  useEffect(() => {
    savePageView({
      type: 'SYSTEMGUIDE',
      ids: [categoryDbName],
    });
  }, []);

  const sections = [
    {
      sectionType: 'single',
      component: {
        componentType: 'guideProcess',
        componentData: {
          processTitle: t('guideHome.process'),
          processItems: [
            {
              number: t('guideHome.processQuestionsNumber'),
              text: t('guideHome.processQuestions'),
            },
            {
              number: t('guideHome.processMinutesNumber'),
              text: t('guideHome.processMinutes'),
            },
            {
              number: '3',
              text: t('guideHome.recommendations'),
            },
          ],
          imageSrc:
            'https://ik.imagekit.io/businesswith/tr:w-508,h-382,dpr-2/systemguide/systemguiden-v2.png',
          imageAlt: 'systemGuideProcessImgAlt',
        },
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'testimonials',
        title: t('guide.testimonialTitle'),
        componentData: [
          {
            name: 'Michael Kärrberg',
            role: t('guideQuote.karrberg.role'),
            company: null,
            testimonial: t('guideQuote.karrberg.text'),
            imgUrl:
              'https://ik.imagekit.io/businesswith/tr:w-40,h-40,dpr-2/testimonials/michael-karrberg.jpeg',
          },
          {
            name: 'Eva Malmberg',
            role: t('guideQuote.malmberg.role'),
            company: null,
            testimonial: t('guideQuote.malmberg.text'),
            imgUrl:
              'https://ik.imagekit.io/businesswith/tr:w-40,h-40,dpr-2/testimonials/eva-malmberg.jpeg',
          },
          {
            name: 'Andreas Rudholm',
            role: t('guideQuote.rudholm.role'),
            company: null,
            testimonial: t('guideQuote.rudholm.text'),
            imgUrl:
              'https://ik.imagekit.io/businesswith/tr:w-40,h-40,dpr-2/testimonials/andreas-rudholm.jpeg',
          },
        ],
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'guideHowItWorks',
        image:
          'https://ik.imagekit.io/businesswith/tr:w-658,h-840,cm-pad_resize/site/homeSectionRight.jpg',
        align: 'right',
        // label: 'Va vi gör',
        title: guideTitle,
        actionButtonText: t('guideHome.startGuide'),
        onActionButtonClick: () => {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
          });
        },
        componentData: {
          strapiText: guideText,
        },
      },
    },
    {
      sectionType: 'single',
      component: {
        type: 'guideControls',
        componentData: {
          onOpenGuide: (companySize) => {
            systemguideRef.current.startSystemGuide({
              companySize,
              category: categoryDbName,
            });
          },
        },
      },
    },
  ];

  return (
    <>
      <SystemGuide
        hero={{
          titleStyled: !!guideH1,
          title: (guideH1 && removeP(guideH1)) || (
            <Trans
              i18nKey="guideHome.titleCategory"
              values={{
                category: categoryName,
              }}
              components={{ bold: <strong /> }}
            />
          ),
          preambleStyled: !!guideIntroText,
          preamble: guideIntroText || (
            <Trans
              i18nKey="guideHome.preamble"
              components={{ bold: <strong className="font-extrabold" /> }}
            />
          ),
          guideRunsText: (
            <Trans
              i18nKey="guideHome.guideRunsText"
              values={{
                number: Number(
                  CONSTANTS_NUMBERS.guideRunsNumber
                ).toLocaleString('sv-SE'),
              }}
              components={{ span: <strong className="font-extrabold" /> }}
            />
          ),
        }}
        sections={sections}
        t={t}
        locale={locale}
        menu={menu}
        footer={footer}
        siteUrls={siteUrls}
        location={location}
        search={search}
      />

      <BusinesswithSystemguide ref={systemguideRef} />
    </>
  );
};

export default SystemGuideTemplate;

export function Head({ data, location, pageContext }) {
  const { t } = useTranslation();
  const { locale } = pageContext;

  const categoryData = useTransformGatsbyData(data.categoryData);
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const { siteUrls } = bwPageBuildData.pageData;
  const { categoryName, seoGuide } = categoryData;

  return (
    <MetatagsForLayout
      title={
        seoGuide?.metaTitle ||
        t('guideSeo.metaTitle', {
          category: categoryName,
          country: t(`shared.localeToCountry.${locale}`),
        })
      }
      description={
        seoGuide?.metaDescription ||
        t('guideSeo.metaDescription', {
          category: categoryName,
        })
      }
      pathname={location.pathname}
      thumbnail="/og-images/systemguiden.png"
      locale={locale}
      siteUrls={siteUrls}
      noindex
    />
  );
}
